<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
type Value = { sales: number; netRevenueMinor: number; estimatedMarginMinor: number | null };
type Report = {
  year: number;
  asOf: string;
  financialReconciliationRequired?: boolean;
  months: Array<Value & { month: number }>;
  offers: Array<Value & { offerId: string; label: string }>;
};
const report = ref<Report | null>(null);
const loading = ref(false);
const error = ref('');
const year = ref(new Date().getUTCFullYear());
const monthNames = [
  'Jan',
  'Fév',
  'Mar',
  'Avr',
  'Mai',
  'Juin',
  'Juil',
  'Août',
  'Sep',
  'Oct',
  'Nov',
  'Déc',
];
const money = (value: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value / 100);
const scale = computed(() =>
  Math.max(
    1,
    ...(report.value?.months ?? []).flatMap((m) => [
      Math.abs(m.netRevenueMinor),
      Math.abs(m.estimatedMarginMinor ?? 0),
    ]),
  ),
);
const offerScale = computed(() =>
  Math.max(1, ...(report.value?.offers ?? []).map((o) => Math.abs(o.netRevenueMinor))),
);
async function load(nextYear = year.value) {
  if (loading.value) return;
  loading.value = true;
  error.value = '';
  report.value = null;
  year.value = nextYear;
  try {
    const response = await globalThis.fetch(`/admin/api/annual-performance?year=${nextYear}`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Indicateurs annuels indisponibles.');
    const body = await response.json();
    if (
      body.year !== nextYear ||
      !Array.isArray(body.months) ||
      body.months.length !== 12 ||
      !Array.isArray(body.offers)
    )
      throw new Error('Indicateurs annuels invalides.');
    report.value = body;
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Indicateurs annuels indisponibles.';
  } finally {
    loading.value = false;
  }
}
onMounted(() => load());
</script>

<template>
  <section class="annual-performance" aria-label="Performance annuelle des offres">
    <h3>Performance annuelle</h3>
    <div class="annual-year">
      <button
        type="button"
        aria-label="Année précédente"
        :disabled="loading || year <= 2000"
        @click="load(year - 1)"
      >
        ←
      </button>
      <strong>{{ year }}</strong>
      <button
        type="button"
        aria-label="Année suivante"
        :disabled="loading || year >= new Date().getUTCFullYear()"
        @click="load(year + 1)"
      >
        →
      </button>
    </div>
    <p>Toutes les offres · commandes payées dans l’année (UTC), ajustées à ce jour.</p>
    <p v-if="loading" role="status">Calcul des graphiques…</p>
    <div v-else-if="error" role="alert">
      {{ error }} <button type="button" @click="load()">Réessayer</button>
    </div>
    <template v-else-if="report">
      <p v-if="report.financialReconciliationRequired" role="alert">
        Des remboursements restent à rapprocher. Seuls les règlements et remboursements
        confirmés sont comptés ; la marge concernée reste indisponible.
      </p>
      <h4>Ventes et marge estimée par mois</h4>
      <p class="annual-legend">
        <span>Bleu : montant net réglé</span><span>Vert : marge estimée · rouge : perte</span>
      </p>
      <svg
        class="annual-chart"
        viewBox="0 0 360 205"
        role="img"
        aria-label="Montants nets réglés et marges estimées sur douze mois ; valeurs détaillées ci-dessous"
      >
        <line x1="0" y1="105" x2="360" y2="105" stroke="#65718a" />
        <g
          v-for="(month, index) in report.months"
          :key="month.month"
          :transform="`translate(${index * 30}, 0)`"
        >
          <title>
            {{ monthNames[index] }} : {{ money(month.netRevenueMinor) }} ; marge
            {{
              month.estimatedMarginMinor === null
                ? 'inconnue'
                : money(month.estimatedMarginMinor)
            }}
          </title>
          <rect
            x="4"
            :y="month.netRevenueMinor >= 0 ? 105 - (month.netRevenueMinor / scale) * 80 : 105"
            width="9"
            :height="(Math.abs(month.netRevenueMinor) / scale) * 80"
            fill="#20214f"
          />
          <rect
            v-if="month.estimatedMarginMinor !== null"
            x="15"
            :y="
              month.estimatedMarginMinor >= 0
                ? 105 - (month.estimatedMarginMinor / scale) * 80
                : 105
            "
            width="9"
            :height="(Math.abs(month.estimatedMarginMinor) / scale) * 80"
            :fill="month.estimatedMarginMinor >= 0 ? '#187344' : '#b91c1c'"
          />
          <text v-else x="19" y="99" text-anchor="middle" font-size="13">?</text>
          <text x="14" y="200" text-anchor="middle" font-size="10">
            {{ monthNames[index] }}
          </text>
        </g>
      </svg>
      <p class="annual-note">
        Marge estimée = montant réglé après compléments et remboursements validés, moins coût
        théorique du catalogue actuel. Frais, charges et coûts réels de préparation non déduits
        : bénéfice net indisponible. « ? » = coûts manquants.
      </p>
      <details>
        <summary>Montants mensuels</summary>
        <ul class="annual-values">
          <li v-for="month in report.months" :key="month.month">
            <strong>{{ monthNames[month.month - 1] }} · {{ month.sales }} vente(s)</strong
            ><span
              >{{ money(month.netRevenueMinor) }} · marge
              {{
                month.estimatedMarginMinor === null
                  ? 'inconnue'
                  : money(month.estimatedMarginMinor)
              }}</span
            >
          </li>
        </ul>
      </details>
      <h4>Performance des offres</h4>
      <p v-if="!report.offers.length">Aucune vente payée pour cette année.</p>
      <ul class="annual-offers">
        <li v-for="offer in report.offers" :key="offer.offerId">
          <strong>{{ offer.label }}</strong>
          <span>{{ offer.sales }} vente(s) · {{ money(offer.netRevenueMinor) }}</span>
          <div class="annual-track" aria-hidden="true">
            <i :style="{ width: `${(Math.abs(offer.netRevenueMinor) / offerScale) * 100}%` }" />
          </div>
          <small
            >Marge estimée :
            {{
              offer.estimatedMarginMinor === null
                ? 'coûts manquants'
                : money(offer.estimatedMarginMinor)
            }}</small
          >
        </li>
      </ul>
    </template>
  </section>
</template>

<style scoped>
.annual-performance {
  margin: 24px 0;
  padding-top: 18px;
  border-top: 1px solid #dce2ed;
  min-width: 0;
  overflow-wrap: anywhere;
}
.annual-year {
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: center;
}
.annual-year button {
  min-width: 44px;
  min-height: 44px;
}
.annual-chart {
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
}
.annual-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 12px;
}
.annual-note {
  font-size: 12px;
  color: #526075;
}
.annual-values,
.annual-offers {
  list-style: none;
  padding: 0;
}
.annual-values li,
.annual-offers li {
  display: grid;
  gap: 6px;
  padding: 12px 0;
  border-bottom: 1px solid #e3e8ef;
}
.annual-track {
  height: 12px;
  background: #e4eaf4;
  border-radius: 6px;
  overflow: hidden;
}
.annual-track i {
  display: block;
  height: 100%;
  background: #20214f;
}
</style>
